import { z } from "zod"

export type FieldConfig = {
  fieldConfigurationActive?: boolean
  subject_active?: boolean
  customer_number_active?: boolean
  customer_number_required?: boolean
  order_number_active?: boolean
  order_number_required?: boolean
  invoice_number_active?: boolean
  invoice_number_required?: boolean
  first_name_active?: boolean
  first_name_required?: boolean
  last_name_active?: boolean
  last_name_required?: boolean
  company_active?: boolean
  company_required?: boolean
  phone_active?: boolean
  phone_required?: boolean
  email_active?: boolean
  email_required?: boolean
  article_active?: boolean
  article_required?: boolean
  quantity_active?: boolean
  quantity_required?: boolean
  reason_for_complaint_active?: boolean
  damage_active?: boolean
  handling_active?: boolean
  handling_required?: boolean
  reason_for_return_active?: boolean
  reason_for_return_required?: boolean
  reaon_for_return_required?: boolean
  return_message_active?: boolean
  return_message_required?: boolean
}

// Allow empty string "" or undefined in non-required inputs
const strOptionalOrEmpty = (schema: z.ZodString) => z.union([schema, z.literal("")]).optional()

export function buildValidationSchema(params: {
  t: (key: string) => string
  fieldConfig?: FieldConfig
  subjectReclamationValue: string
  orderNumberMissing?: string
  articleMissing?: string
  quantityMissing?: string
}) {
  const { t, fieldConfig, subjectReclamationValue } = params
  const fc: FieldConfig = fieldConfig ?? {}
  const cfgActive = !!fc.fieldConfigurationActive
  const req = (flag?: boolean) => !!flag && cfgActive

  const validationShape = {
    ReclamationForm_CustomerId: req(fc.customer_number_required)
      ? z
          .string()
          .max(32, t("forms.contact_us_generic_form.error.customer_id_max"))
          .min(1, t("forms.contact_us_generic_form.error.customer_id"))
      : strOptionalOrEmpty(z.string().max(32, t("forms.contact_us_generic_form.error.customer_id_max"))),

    ReclamationForm_OrderNumber: req(fc.order_number_required)
      ? z.string().min(1, params.orderNumberMissing ?? t("forms.contact_us_generic_form.error.order_number"))
      : strOptionalOrEmpty(z.string()),

    ReclamationForm_InvoiceNumber: req(fc.invoice_number_required)
      ? z.string().min(1, " ")
      : z.string().optional(),

    ReclamationForm_FirstName: req(fc.first_name_required)
      ? z
          .string()
          .min(2, t("forms.contact_us_generic_form.error.first_name"))
          .max(64, t("forms.contact_us_generic_form.error.first_name_max"))
      : strOptionalOrEmpty(
          z.string().min(2, t("forms.contact_us_generic_form.error.first_name")).max(64, t("forms.contact_us_generic_form.error.first_name_max"))
        ),

    ReclamationForm_LastName: req(fc.last_name_required)
      ? z
          .string()
          .min(2, t("forms.contact_us_generic_form.error.last_name"))
          .max(64, t("forms.contact_us_generic_form.error.last_name_max"))
      : strOptionalOrEmpty(
          z.string().min(2, t("forms.contact_us_generic_form.error.last_name")).max(64, t("forms.contact_us_generic_form.error.last_name_max"))
        ),

    ReclamationForm_CompanyName: req(fc.company_required) ? z.string().min(1, " ") : z.string().optional(),

    ReclamationForm_Phone: req(fc.phone_required)
      ? z
          .string()
          .min(2, t("forms.contact_us_generic_form.error.phone"))
          .max(32, t("forms.contact_us_generic_form.error.phone_max"))
      : strOptionalOrEmpty(
          z.string().min(2, t("forms.contact_us_generic_form.error.phone")).max(32, t("forms.contact_us_generic_form.error.phone_max"))
        ),

    ReclamationForm_Email: req(fc.email_required)
      ? z.string().email(t("forms.contact_us_generic_form.error.email"))
      : z.union([z.string().email(t("forms.contact_us_generic_form.error.email")), z.literal("")]).optional(),

    ReclamationForm_Subject: z.string().min(1, t("forms.contact_us_generic_form.error.subject")),
    ReclamationForm_ReclamationArticle: z.string().optional(),
    ReclamationForm_ReclamationQuantity: z.string().optional(),
    ReclamationForm_ReclamationReason: z.string().optional(),
    ReclamationForm_ReclamationDamage: z.string().optional(),
    ReclamationForm_ReclamationMessage: z.string().optional(),
    ReclamationForm_ReclamationHandling: z.string().optional(),
    ReclamationForm_ReturnArticle: z.string().optional(),
    ReclamationForm_ReturnQuantity: z.string().optional(),
    ReclamationForm_ReturnReason: z.string().optional(),
    ReclamationForm_ReturnMessage: z.string().optional(),

    ReclamationForm_AcceptTerms: z
      .boolean()
      .refine((val) => val === true, { message: t("forms.contact_us_generic_form.error.terms") }),
  }

  return z.object(validationShape).superRefine((value, context) => {
    const isReclamation = value.ReclamationForm_Subject === subjectReclamationValue

    if (isReclamation) {
      const articleActive = !cfgActive || !!fc.article_active
      const articleReq = cfgActive ? !!fc.article_required : true
      if (articleActive && articleReq && !value.ReclamationForm_ReclamationArticle) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: params.articleMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationArticle"],
        })
      }

      const qtyActive = !cfgActive || !!fc.quantity_active
      const qtyReq = cfgActive ? !!fc.quantity_required : true
      if (qtyActive && qtyReq && !value.ReclamationForm_ReclamationQuantity) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: params.quantityMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationQuantity"],
        })
      }

      const reasonActive = !cfgActive || !!fc.reason_for_complaint_active
      const reasonReq = cfgActive ? !!(fc as any).reason_for_complaint_required : true
      if (reasonActive && reasonReq && !value.ReclamationForm_ReclamationReason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationReason"],
        })
      }

      const damageActive = !cfgActive || !!fc.damage_active
      const damageReq = cfgActive ? !!(fc as any).damage_required : true
      if (damageActive && damageReq && !value.ReclamationForm_ReclamationDamage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationDamage"],
        })
      }

      const msgActive = !cfgActive || !!(fc as any).reclamation_message_active
      const msgReq = cfgActive ? !!(fc as any).reclamation_message_required : true
      if (msgActive && msgReq && !value.ReclamationForm_ReclamationMessage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationMessage"],
        })
      }

      const handlingActive = !cfgActive || !!fc.handling_active
      const handlingReq = cfgActive ? !!fc.handling_required : true
      if (handlingActive && handlingReq && !value.ReclamationForm_ReclamationHandling) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationHandling"],
        })
      }
    } else {
      const articleActive = !cfgActive || !!fc.article_active
      const articleReq = cfgActive ? !!fc.article_required : true
      if (articleActive && articleReq && !value.ReclamationForm_ReturnArticle) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: params.articleMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnArticle"],
        })
      }
      const qtyActive = !cfgActive || !!fc.quantity_active
      const qtyReq = cfgActive ? !!fc.quantity_required : true
      if (qtyActive && qtyReq && !value.ReclamationForm_ReturnQuantity) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: params.quantityMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnQuantity"],
        })
      }
      const rrActive = !cfgActive || !!fc.reason_for_return_active
      const rrReq = cfgActive ? !!(fc.reason_for_return_required ?? fc.reaon_for_return_required) : true
      if (rrActive && rrReq && !value.ReclamationForm_ReturnReason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnReason"],
        })
      }

      const rmsgActive = !cfgActive || !!fc.return_message_active
      const rmsgReq = cfgActive ? !!fc.return_message_required : false
      if (rmsgActive && rmsgReq && !value.ReclamationForm_ReturnMessage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnMessage"],
        })
      }
    }
  })
}

export const sanitizeField = (text: string): string =>
  text.replaceAll("\r\n", "\\n").replaceAll("\n", "\\n").replaceAll('"', '\\"')

export const replacePlaceholder = (text: string, value: any, subjectReclamationValue: string): string => {
  const now = new Date()
  const currentDate = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(now)
  const currentTime = new Intl.DateTimeFormat("de-DE", { timeStyle: "medium" }).format(now)

  return text
    .replaceAll("${Date}", currentDate)
    .replaceAll("${Time}", currentTime)
    .replaceAll("${CustomerId}", sanitizeField(value.ReclamationForm_CustomerId ?? ""))
    .replaceAll("${OrderNumber}", sanitizeField(value.ReclamationForm_OrderNumber ?? ""))
    .replaceAll("${InvoiceNumber}", sanitizeField(value.ReclamationForm_InvoiceNumber ?? ""))
    .replaceAll("${FirstName}", sanitizeField(value.ReclamationForm_FirstName ?? ""))
    .replaceAll("${LastName}", sanitizeField(value.ReclamationForm_LastName ?? ""))
    .replaceAll("${Company}", sanitizeField(value.ReclamationForm_CompanyName ?? ""))
    .replaceAll("${Phone}", sanitizeField(value.ReclamationForm_Phone ?? ""))
    .replaceAll("${Email}", sanitizeField(value.ReclamationForm_Email ?? ""))
    .replaceAll("${Subject}", sanitizeField(value.ReclamationForm_Subject ?? ""))
    .replaceAll(
      "${Article}",
      value.ReclamationForm_Subject === subjectReclamationValue
        ? sanitizeField(value.ReclamationForm_ReclamationArticle ?? "")
        : sanitizeField(value.ReclamationForm_ReturnArticle ?? "")
    )
    .replaceAll(
      "${Quantity}",
      value.ReclamationForm_Subject === subjectReclamationValue
        ? sanitizeField(value.ReclamationForm_ReclamationQuantity ?? "")
        : sanitizeField(value.ReclamationForm_ReturnQuantity ?? "")
    )
    .replaceAll(
      "${Reason}",
      value.ReclamationForm_Subject === subjectReclamationValue
        ? sanitizeField(value.ReclamationForm_ReclamationReason ?? "")
        : sanitizeField(value.ReclamationForm_ReturnReason ?? "")
    )
    .replaceAll(
      "${Damage}",
      value.ReclamationForm_Subject === subjectReclamationValue
        ? sanitizeField(value.ReclamationForm_ReclamationDamage ?? "")
        : "-"
    )
    .replaceAll(
      "${Message}",
      value.ReclamationForm_Subject === subjectReclamationValue
        ? sanitizeField(value.ReclamationForm_ReclamationMessage ?? "")
        : sanitizeField(value.ReclamationForm_ReturnMessage ?? "")
    )
    .replaceAll(
      "${Handling}",
      value.ReclamationForm_Subject === subjectReclamationValue
        ? sanitizeField(value.ReclamationForm_ReclamationHandling ?? "")
        : "-"
    )
}
